import React from 'react';
import { Post } from '../../services/postService';
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Clock } from 'lucide-react';
import TikTokIcon from '../icons/TikTokIcon';

interface EventComponentProps {
  event: {
    title: string;
    resource: Post;
  };
}

const platformIcons: { [key: string]: React.ElementType } = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
  tiktok: TikTokIcon,
};

const platformColors: { [key: string]: { bg: string; text: string; accent: string } } = {
  facebook: { bg: 'bg-blue-600', text: 'text-blue-100', accent: 'border-blue-400' },
  instagram: { bg: 'bg-gradient-to-r from-purple-600 to-pink-600', text: 'text-pink-100', accent: 'border-pink-400' },
  twitter: { bg: 'bg-sky-500', text: 'text-sky-100', accent: 'border-sky-300' },
  linkedin: { bg: 'bg-blue-700', text: 'text-blue-100', accent: 'border-blue-400' },
  youtube: { bg: 'bg-orange-500', text: 'text-orange-100', accent: 'border-orange-400' },
  tiktok: { bg: 'bg-black', text: 'text-white', accent: 'border-gray-400' },
};

const EventComponent: React.FC<EventComponentProps> = ({ event }) => {
  const { resource: post } = event;
  
  // Get primary platform for styling
  const primaryPlatform = post.platforms?.[0]?.toLowerCase() || 'default';
  const platformConfig = platformColors[primaryPlatform] || { 
    bg: 'bg-slate-600', 
    text: 'text-slate-100', 
    accent: 'border-slate-400' 
  };

  return (
    <div className="relative h-full w-full group cursor-pointer">
      {/* Sleek Bar Design */}
      <div className={`
        relative h-full w-full rounded-lg overflow-hidden
        ${platformConfig.bg} ${platformConfig.text}
        shadow-lg hover:shadow-xl
        transform hover:scale-105 transition-all duration-200
        border-l-4 ${platformConfig.accent}
      `}>
        {/* Subtle Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-white/10"></div>
        
        {/* Content Container */}
        <div className="relative h-full flex items-center justify-between px-3 py-2">
          {/* Left Side - Platform Icons */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {post.platforms?.slice(0, 2).map((platform, index) => {
              const Icon = platformIcons[platform.toLowerCase()];
              return Icon ? (
                <div
                  key={platform}
                  className="w-5 h-5 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
                  title={platform}
                >
                  <Icon className="w-3 h-3 text-white" />
                </div>
              ) : (
                <div
                  key={platform}
                  className="w-5 h-5 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
                  title={platform}
                >
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              );
            })}
            {post.platforms && post.platforms.length > 2 && (
              <div className="w-5 h-5 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">+{post.platforms.length - 2}</span>
              </div>
            )}
          </div>

          {/* Center - Title */}
          <div className="flex-1 min-w-0 mx-3">
            <div className="text-xs font-semibold text-white truncate">
              {event.title}
            </div>
          </div>

          {/* Right Side - Time */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Clock className="w-3 h-3 text-white/80" />
            <span className="text-xs text-white/90 font-medium">
              {new Date(post.scheduled_at || '').toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </div>
        </div>

        {/* Status Indicator */}
        <div className="absolute top-1 right-1">
          {post.status === 'scheduled' && (
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-sm" title="Scheduled"></div>
          )}
          {post.status === 'published' && (
            <div className="w-2 h-2 bg-green-400 rounded-full shadow-sm" title="Published"></div>
          )}
        </div>

        {/* Hover Shine Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-700"></div>
      </div>
    </div>
  );
};

export default EventComponent;